import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  dateToPartialDate,
  withDisplayYear,
} from "@/components/date-filter/date-filter-presets.ts";
import {
  ApiDateFilter,
  ApiDateFilterMode,
  ApiDateFilterOperator,
} from "@/components/date-filter/date-filter.type.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiPartialDate } from "@/types/common.types.ts";
import { Box, Button, Modal, ModalProps, Radio, Stack, Title, useModals } from "@skailer-com/uikit";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ChoosePeriodModalProps extends ModalProps {
  value: ApiDateFilter | undefined;
  availableModes: ApiDateFilterMode[];
  onSave: (value: ApiDateFilter | undefined) => void;
}

export function ChoosePeriodModal({
  value,
  availableModes,
  onSave,
  ...props
}: ChoosePeriodModalProps) {
  const { id } = props;
  const { t } = useTranslation();
  const { close } = useModals();
  const { buildFromPartialDate } = useFormatter();

  const initialMode = value && availableModes.includes(value.mode) ? value.mode : availableModes[0];
  const isCustom = value?.operator === ApiDateFilterOperator.custom;

  const [mode, setMode] = useState<ApiDateFilterMode>(initialMode);
  const [from, setFrom] = useState<ApiPartialDate | undefined>(
    isCustom ? (value?.value_from ?? undefined) : undefined
  );
  const [to, setTo] = useState<ApiPartialDate | undefined>(
    isCustom ? (value?.value_to ?? undefined) : undefined
  );

  const toInputRef = useRef<HTMLInputElement>(null);

  const onSubmit = () => {
    onSave({
      mode,
      operator: ApiDateFilterOperator.custom,
      value_from: from,
      value_to: to,
      preset: undefined,
    });
    close(id);
  };

  const modeOptions: { mode: ApiDateFilterMode; title: string; caption: string }[] = [
    {
      mode: ApiDateFilterMode.date,
      title: t("common:period_type_calendar"),
      caption: t("common:period_type_calendar_caption"),
    },
    {
      mode: ApiDateFilterMode.day,
      title: t("common:period_type_annual"),
      caption: t("common:period_type_annual_caption"),
    },
  ].filter((option) => availableModes.includes(option.mode));

  const showYear = mode === ApiDateFilterMode.date;

  const fromDate = buildFromPartialDate(withDisplayYear(from));
  const toDate = buildFromPartialDate(withDisplayYear(to));
  const rangeError =
    mode === ApiDateFilterMode.date && fromDate && toDate && fromDate.getTime() > toDate.getTime()
      ? t("errors:end_date_should_be_greater_start_date")
      : undefined;

  return (
    <Modal
      {...props}
      title={t("common:choose_period")}
      withCloser
      size={"md"}
      actions={
        <Stack gap="sm">
          <Button type={"button"} size={"lg"} onClick={onSubmit} disabled={Boolean(rangeError)}>
            {t("common:save")}
          </Button>
          <Button variant="plain" size="lg" onClick={() => close(id)}>
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"xl"}>
        {modeOptions.length > 1 && (
          <Stack gap={"sm"}>
            <Title header={t("common:filter_type")} size={"xs"} variant="secondary" />
            {modeOptions.map((option) => (
              <Radio
                key={option.mode}
                name={"period-filter-type"}
                title={option.title}
                caption={option.caption}
                checked={mode === option.mode}
                onChange={() => setMode(option.mode)}
              />
            ))}
          </Stack>
        )}

        <Stack gap={"sm"}>
          <Title header={t("common:choose_period")} size={"xs"} variant="secondary" />
          <Box className={"grid grid-cols-2 gap-sm"}>
            <Datepicker
              isClearable
              showYearDropdown={showYear}
              value={fromDate}
              maxDate={showYear ? toDate : undefined}
              label={t("common:date_from_short")}
              onChange={(date: Date | null) => {
                setFrom(date ? dateToPartialDate(date, mode) : undefined);
                if (date && !to) {
                  requestAnimationFrame(() => toInputRef.current?.focus());
                }
              }}
            />
            <Datepicker
              isClearable
              showYearDropdown={showYear}
              value={toDate}
              minDate={showYear ? fromDate : undefined}
              openToDate={fromDate}
              inputRef={toInputRef}
              label={t("common:date_to_short")}
              error={rangeError}
              onChange={(date: Date | null) =>
                setTo(date ? dateToPartialDate(date, mode) : undefined)
              }
            />
          </Box>
        </Stack>
      </Stack>
    </Modal>
  );
}
